import { useState, useEffect } from 'react';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { navItems } from '@/data/siteData';
import { useTheme } from '@/hooks/useTheme';
import { Button } from '@/components/ui/button';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('#home');
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return;
    }

    const sections = navItems.map(item => document.querySelector(item.href)).filter(Boolean) as Element[];
    
    const observerOptions = {
      root: null,
      rootMargin: '-30% 0px -60% 0px',
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(`#${entry.target.id}`);
        }
      });
    }, observerOptions);

    sections.forEach(section => observer.observe(section));

    return () => {
      sections.forEach(section => observer.unobserve(section));
    };
  }, []);

  const handleNavClick = (href: string) => {
    setIsOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 flex items-center border-b border-border bg-background"
      style={{ height: 'var(--navbar-height)', pointerEvents: 'none' }}
    >
      <div
        className={`container-site mx-auto flex items-center justify-between transition-all duration-300 ${
          isScrolled
            ? 'max-w-6xl py-2 px-6'
            : 'max-w-7xl py-3 px-8'
        } pointer-events-auto`}
      >
        {/* Logo */}
        <div className="lg:flex-1 flex justify-start">
          <a
            href="#home"
            onClick={(e) => {
              e.preventDefault();
              handleNavClick('#home');
            }}
            className="font-sans font-extrabold text-sm sm:text-base tracking-[0.12em] uppercase text-foreground hover:opacity-85 transition-opacity flex items-center gap-1"
          >
            <span>MAKE IT</span>
            <span className="border border-foreground bg-foreground text-background px-1.5 py-0.5 font-black text-xs">EASY+</span>
          </a>
        </div>

        {/* Center Desktop Navigation Links */}
        <div className="hidden lg:flex lg:flex-1 justify-center items-center gap-1.5">
          {navItems.filter(item => item.label !== 'Contact').map((item) => {
            const isActive = activeSection === item.href;
            return (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(item.href);
                }}
                className={`px-3 py-1 text-xs uppercase tracking-wider font-bold transition-all duration-200 rounded-none border ${
                  isActive
                    ? 'text-background bg-foreground border-foreground'
                    : 'text-muted-foreground border-transparent hover:text-foreground hover:bg-muted/50'
                }`}
              >
                {item.label}
              </a>
            );
          })}
        </div>

        {/* Right Actions / Mobile Controls */}
        <div className="lg:flex-1 flex justify-end items-center gap-1.5">
          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-1.5">
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-none border border-border h-8 w-8 hover:bg-muted"
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            >
              {theme === 'light' ? (
                <Moon className="h-4 w-4" />
              ) : (
                <Sun className="h-4 w-4" />
              )}
            </Button>

            {/* Contact Button */}
            <Button
              size="sm"
              onClick={() => handleNavClick('#contact')}
              className="h-8 px-4 rounded-none bg-foreground text-background border border-foreground hover:bg-background hover:text-foreground font-bold uppercase transition-all duration-200"
            >
              Contact
            </Button>
          </div>

          {/* Mobile Controls */}
          <div className="flex lg:hidden items-center gap-1.5">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-none border border-border h-8 w-8"
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            >
              {theme === 'light' ? (
                <Moon className="h-4 w-4" />
              ) : (
                <Sun className="h-4 w-4" />
              )}
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(!isOpen)}
              className="rounded-none border border-border h-8 w-8"
              aria-label="Toggle menu"
              aria-expanded={isOpen}
            >
              {isOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden absolute left-0 right-0 mt-0 bg-background border-b border-border shadow-md transition-all duration-300 overflow-hidden pointer-events-auto ${
          isOpen ? 'max-h-[420px] opacity-100 translate-y-0' : 'max-h-0 opacity-0 -translate-y-2 pointer-events-none'
        }`}
        style={{ top: '100%' }}
      >
        <div className="p-4 flex flex-col gap-1.5">
          {navItems.map((item) => {
            if (item.label === 'Contact') {
              return (
                <Button
                  key={item.href}
                  onClick={() => handleNavClick(item.href)}
                  className="mt-2 w-full h-10 rounded-none bg-foreground text-background border border-foreground font-bold uppercase"
                >
                  Contact
                </Button>
              );
            }
            return (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(item.href);
                }}
                className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground hover:bg-muted transition-all border border-transparent hover:border-border"
              >
                {item.label}
              </a>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;